import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, GitBranch } from 'lucide-react';
import { ACTIONS, ACTION_LABELS, OP_LABELS, canBeTarget, canControl, describeRule, nextRuleId, operatorsFor, validateRules } from '@/lib/formRules';

const NEEDS_VALUE = (op) => op && op !== 'is_empty' && op !== 'not_empty';
const ACTION_HELP = { show: 'Targets are hidden unless this condition is met.', hide: 'Targets are hidden while this condition is met (hide always wins over show).', require: 'Targets become required while this condition is met (hidden fields are never required).' };

function ToggleList({ options, value, onToggle, idPrefix, labelledBy, disabledKeys = new Set() }) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-labelledby={labelledBy}>
      {options.map(o => {
        const checked = value.includes(o.value);
        return (
          <Button key={o.value} type="button" role="checkbox" aria-checked={checked} size="sm" variant={checked ? 'default' : 'outline'} className="h-8 text-xs"
            disabled={disabledKeys.has(o.value)} onClick={() => onToggle(o.value)} data-testid={`${idPrefix}-${o.value}`}>{o.label}</Button>
        );
      })}
    </div>
  );
}

function RuleDialog({ open, onOpenChange, fields, rules, initial, onSave, restoreFocusTo }) {
  const keyed = useMemo(() => fields.filter(f => f.key), [fields]);
  const [when, setWhen] = useState({ field: '', op: '', value: null });
  const [then, setThen] = useState({ action: 'show', fields: [] });
  const [errors, setErrors] = useState([]);
  useEffect(() => {
    if (!open) return;
    setWhen(initial ? { ...initial.when } : { field: '', op: '', value: null });
    setThen(initial ? { action: initial.then.action, fields: [...initial.then.fields] } : { action: 'show', fields: [] });
    setErrors([]);
  }, [open, initial]);

  const ctl = keyed.find(f => f.key === when.field);
  const ops = ctl ? operatorsFor(ctl) : [];
  const targets = keyed.filter(f => canBeTarget(f, then.action) && f.key !== when.field);
  const setField = (k) => { const f = keyed.find(x => x.key === k); const nextOps = operatorsFor(f); setWhen({ field: k, op: nextOps.includes(when.op) ? when.op : '', value: null }); setThen(t => ({ ...t, fields: t.fields.filter(x => x !== k) })); };
  const setOp = (op) => setWhen(w => ({ ...w, op, value: op === 'in' ? [] : NEEDS_VALUE(op) ? '' : null }));
  const setAction = (action) => setThen(t => ({ action, fields: t.fields.filter(k => canBeTarget(keyed.find(f => f.key === k), action)) }));
  const toggleTarget = (k) => setThen(t => ({ ...t, fields: t.fields.includes(k) ? t.fields.filter(x => x !== k) : [...t.fields, k] }));
  const toggleIn = (v) => setWhen(w => { const cur = Array.isArray(w.value) ? w.value : []; return { ...w, value: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v] }; });

  const save = () => {
    const rule = { id: initial?.id || nextRuleId(rules), when: { field: when.field, op: when.op, value: NEEDS_VALUE(when.op) ? when.value : null }, then: { action: then.action, fields: then.fields } };
    const next = initial ? rules.map(r => r.id === initial.id ? rule : r) : [...rules, rule];
    const errs = validateRules(fields, next).filter(e => e.rule_id === rule.id || e.rule_id == null);
    if (errs.length) { setErrors(errs); return; }
    onSave(next); onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="rule-dialog" onOpenAutoFocus={(e) => { e.preventDefault(); document.getElementById('rule-when-field')?.focus(); }}
        onCloseAutoFocus={(e) => { e.preventDefault(); restoreFocusTo?.current?.focus?.(); }}>
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit condition' : 'Add condition'}</DialogTitle>
          <DialogDescription>Choose the controlling question, how it is answered, and what happens to other fields. Field keys are never typed here.</DialogDescription>
        </DialogHeader>
        {errors.length > 0 && (
          <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800" data-testid="rule-dialog-errors">
            <ul className="list-disc pl-4">{errors.map((e, i) => <li key={i}>{e.message}</li>)}</ul>
          </div>
        )}
        <div className="grid gap-3">
          <div>
            <Label htmlFor="rule-when-field" className="text-xs">When this field</Label>
            <Select value={when.field} onValueChange={setField}>
              <SelectTrigger id="rule-when-field" className="h-9 text-sm" data-testid="rule-when-field"><SelectValue placeholder="Select a field…" /></SelectTrigger>
              <SelectContent>{keyed.filter(canControl).map(f => <SelectItem key={f.key} value={f.key} data-testid={`rule-when-field-${f.key}`}>{f.label || f.key}</SelectItem>)}</SelectContent>
            </Select>
            {keyed.length < fields.length && <p className="text-[11px] text-muted-foreground mt-1">Fields without a key are not listed — set a field key first.</p>}
          </div>
          <div>
            <Label htmlFor="rule-when-op" className="text-xs">Condition</Label>
            <Select value={when.op} onValueChange={setOp} disabled={!ctl}>
              <SelectTrigger id="rule-when-op" className="h-9 text-sm" data-testid="rule-when-op"><SelectValue placeholder={ctl ? 'Select…' : 'Choose a field first'} /></SelectTrigger>
              <SelectContent>{ops.map(op => <SelectItem key={op} value={op} data-testid={`rule-when-op-${op}`}>{OP_LABELS[op]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {ctl && NEEDS_VALUE(when.op) && (
            <div>
              <Label id="rule-when-value-label" htmlFor="rule-when-value" className="text-xs">Value</Label>
              {when.op === 'in' && ctl.options?.length > 0 ? (
                <ToggleList options={ctl.options.map(o => ({ value: o, label: o }))} value={Array.isArray(when.value) ? when.value : []} onToggle={toggleIn} idPrefix="rule-when-value" labelledBy="rule-when-value-label" />
              ) : ctl.options?.length > 0 ? (
                <Select value={typeof when.value === 'string' ? when.value : ''} onValueChange={v => setWhen(w => ({ ...w, value: v }))}>
                  <SelectTrigger id="rule-when-value" className="h-9 text-sm" data-testid="rule-when-value"><SelectValue placeholder="Select an option…" /></SelectTrigger>
                  <SelectContent>{ctl.options.map(o => <SelectItem key={o} value={o} data-testid={`rule-when-value-${o}`}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : ctl.type === 'yes_no' ? (
                <Select value={typeof when.value === 'string' ? when.value : ''} onValueChange={v => setWhen(w => ({ ...w, value: v }))}>
                  <SelectTrigger id="rule-when-value" className="h-9 text-sm" data-testid="rule-when-value"><SelectValue placeholder="Yes or No" /></SelectTrigger>
                  <SelectContent><SelectItem value="Yes" data-testid="rule-when-value-Yes">Yes</SelectItem><SelectItem value="No" data-testid="rule-when-value-No">No</SelectItem></SelectContent>
                </Select>
              ) : when.op === 'in' ? (
                <Input id="rule-when-value" className="h-9 text-sm" placeholder="Comma-separated values" value={Array.isArray(when.value) ? when.value.join(', ') : ''} onChange={e => setWhen(w => ({ ...w, value: e.target.value.split(',').map(x => x.trim()).filter(Boolean) }))} data-testid="rule-when-value" />
              ) : (
                <Input id="rule-when-value" className="h-9 text-sm" value={typeof when.value === 'string' ? when.value : ''} onChange={e => setWhen(w => ({ ...w, value: e.target.value }))} data-testid="rule-when-value" />
              )}
            </div>
          )}
          <div>
            <Label htmlFor="rule-then-action" className="text-xs">Then</Label>
            <Select value={then.action} onValueChange={setAction}>
              <SelectTrigger id="rule-then-action" className="h-9 text-sm" data-testid="rule-then-action"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTIONS.map(a => <SelectItem key={a} value={a} data-testid={`rule-then-action-${a}`}>{ACTION_LABELS[a]}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">{ACTION_HELP[then.action]}</p>
          </div>
          <div>
            <Label id="rule-then-fields-label" className="text-xs">These fields</Label>
            {targets.length ? <ToggleList options={targets.map(f => ({ value: f.key, label: f.label || f.key }))} value={then.fields} onToggle={toggleTarget} idPrefix="rule-then-field" labelledBy="rule-then-fields-label" />
              : <p className="text-xs text-muted-foreground">No eligible target fields.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="rule-dialog-cancel">Cancel</Button>
          <Button type="button" onClick={save} disabled={!when.field || !when.op || !then.fields.length} data-testid="rule-dialog-save">{initial ? 'Save condition' : 'Add condition'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Conditions editor for the template builder. The engine marker is never editable here: the first executable rule sets
// rules_engine_version 1 on save and removing every rule keeps it (rules_json: []).
export function RuleEditor({ fields, rules, onChange, engineVersion }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const openerRef = useRef(null);
  const openDialog = (rule) => { openerRef.current = document.activeElement; setEditing(rule); setOpen(true); };
  const errors = useMemo(() => validateRules(fields, rules), [fields, rules]);
  const errFor = (id) => errors.filter(e => e.rule_id === id || (e.rule_id == null && (e.code === 'RULE_CYCLE' || e.code === 'RULE_DEPTH_EXCEEDED')));
  const remove = (id) => onChange(rules.filter(r => r.id !== id));
  const willActivate = engineVersion === 1 || rules.length > 0;

  return (
    <section aria-labelledby="rule-editor-title" data-testid="rule-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-primary/70" aria-hidden="true" />
          <h3 id="rule-editor-title" className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Conditions ({rules.length})</h3>
          {willActivate && <Badge variant="secondary" className="text-[10px]" data-testid="rule-engine-badge">Rules engine v{engineVersion || 1}</Badge>}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => openDialog(null)} data-testid="rule-add-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add condition</Button>
      </div>
      <p className="text-xs text-muted-foreground mb-2">
        {willActivate ? 'This version evaluates conditions on save and on submit (server-enforced). Removing every condition keeps the rules engine version.' : 'No conditions. Adding the first condition switches this version to the rules engine (v1).'}
      </p>
      {rules.length > 0 && (
        <ul className="space-y-1.5" data-testid="rule-list">
          {rules.map(r => {
            const errs = errFor(r.id);
            return (
              <li key={r.id} className={`rounded-md border px-3 py-2 text-sm flex items-start gap-2 ${errs.length ? 'border-red-300 bg-red-50' : 'bg-card'}`} data-testid={`rule-item-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <span data-testid={`rule-sentence-${r.id}`}>{describeRule(r, fields)}</span>
                  {errs.length > 0 && <ul className="text-xs text-red-700 mt-1 list-disc pl-4" data-testid={`rule-errors-${r.id}`}>{errs.map((e, i) => <li key={i}>{e.message}</li>)}</ul>}
                </div>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" aria-label={`Edit condition ${r.id}`} onClick={() => openDialog(r)} data-testid={`rule-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" aria-label={`Remove condition ${r.id}`} onClick={() => remove(r.id)} data-testid={`rule-remove-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </li>
            );
          })}
        </ul>
      )}
      <RuleDialog open={open} onOpenChange={setOpen} fields={fields} rules={rules} initial={editing} onSave={onChange} restoreFocusTo={openerRef} />
    </section>
  );
}
